import React, { useEffect, useState } from "react";
import { Field, Form, Formik, useFormikContext } from "formik";
import csc from "country-state-city";
import * as yup from "yup";

import { Input, Select } from "../../../../_metronic/_partials/controls";
import { useSchoolContext } from "./SchoolDataContext";
import {
  getFormattedNumber,
  resolveCountry,
  resolveState,
  getStateList,
} from "../../../helpers/Formatter";

// Regex for format: (123) - 456 - 7890
const phoneRegExp = /^\(\d{3}\) - \d{3} - \d{4}$/;

// Countries with no postal system; zip is not required for these. Mirrors
// NO_POSTAL_CODE_COUNTRIES in routes/school-information.js on the server.
const NO_POSTAL_CODE_COUNTRIES = ["jamaica"];
const hasNoPostalCode = (country) =>
  NO_POSTAL_CODE_COUNTRIES.includes(String(country || "").trim().toLowerCase());

// Validation schema. The address is required: it feeds the public map and the
// school-program listing, so a school without one is unusable downstream.
const SchoolEditSchema = yup.object().shape({
  active_id: yup.string().required("Status is required"),
  school_name: yup.string().required("School name is required"),
  school_address: yup.string().required("Street address is required"),
  city: yup.string().required("City is required"),
  country: yup.string().required("Country is required"),
  state: yup.string().required("State/Province is required"),
  zip: yup.string().when("country", (country, schema) =>
    hasNoPostalCode(country)
      ? schema
      : schema.required("Zip/Postal Code is required")
  ),
  // The website shows the school's phone and email to parents for a
  // contact-only program, so both must be on file.
  primary_email: yup
    .string()
    .email("Email Address is not valid")
    .required("Primary email is required"),
  primary_phone: yup
    .string()
    .matches(phoneRegExp, "Phone number must be 10 digits")
    .required("Primary phone is required"),
});

// Keeps the country/state <Select>s in sync with the loaded values on edit:
// resolves the saved country into the selectedCountry object and canonicalizes
// the saved state to the option-value form so it preselects. Mirrors the
// pattern in Users/Components/UserEditForm.js.
function AddressSync({ selectedCountry, setSelectedCountry }) {
  const formik = useFormikContext();

  useEffect(() => {
    if (formik && formik.values && formik.values.country) {
      const country = resolveCountry(formik.values.country);
      if (country) setSelectedCountry(country);
    }
  }, [formik.values.country]);

  useEffect(() => {
    if (!selectedCountry || !formik.values.state) return;
    const s = resolveState(selectedCountry.isoCode, formik.values.state);
    if (!s) return;
    const canonical = selectedCountry.isoCode === "JM" ? s.name : s.isoCode;
    if (canonical !== formik.values.state) {
      formik.setFieldValue("state", canonical);
    }
  }, [selectedCountry]);

  return null;
}

export function SchoolEditForm({
  schoolValues,
  btnRef,
  saveSchool,
  isLoading,
  setSuccessSnackbarMessage,
  setErrorSnackbarMessage,
}) {
  const DROPDOWN_ACTIVE = useSchoolContext().ACTIVE;
  const current_branch = useSchoolContext().currentBranch;

  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();

  // Limit to the countries the business operates in (matches the user form).
  useEffect(() => {
    if (countries.length === 0) {
      setCountries(
        csc
          .getAllCountries()
          .filter((c) => ["US", "CA", "JM"].includes(c.isoCode))
      );
    }
  }, [countries.length]);

  useEffect(() => {
    if (selectedCountry) setStates(getStateList(selectedCountry.isoCode));
  }, [selectedCountry]);

  // Format the initial values
  const formattedInitialValues = {
    ...schoolValues,
    primary_phone: schoolValues.primary_phone
      ? getFormattedNumber(schoolValues.primary_phone)
      : ""
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={formattedInitialValues}
        validationSchema={SchoolEditSchema}
        onSubmit={(values) => {
          // Convert formatted phone back to 10 digits for DB
          const submitValues = {
            ...values,
            primary_phone: values.primary_phone ? values.primary_phone.replace(/[^\d]/g, "") : ""
          };
          saveSchool(submitValues);
        }}
      >
        {({
          getFieldProps,
          handleSubmit,
          setFieldValue,
          values,
          errors,
          touched,
        }) => (
          <fieldset className="form-group" disabled={isLoading}>
            <Form className="form form-label-right">
              <AddressSync
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
              />

              <div className="form-group row">
                <div className="col-lg-12 py-1">
                  <Field
                    name="school_name"
                    label="School Name"
                    labelClasses="required-field"
                    placeholder="Enter school name"
                    type="text"
                    component={Input}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-lg-12 py-1">
                  <Field
                    name="school_address"
                    label="Street Address"
                    labelClasses="required-field"
                    placeholder="Enter street address"
                    type="textarea"
                    component={Input}
                    rows={2}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-lg-3 py-1">
                  <Field
                    name="city"
                    label="City"
                    labelClasses="required-field"
                    placeholder="City"
                    type="text"
                    component={Input}
                  />
                </div>
                <div className="col-lg-3 py-1">
                  <Select
                    name="country"
                    label="Country"
                    labelClasses="required-field"
                    placeholder="Select Country..."
                    options={countries.map((country) => ({
                      value: country.name,
                      label: country.name,
                    }))}
                    onChange={(option) => {
                      setFieldValue("country", option.value);
                      setFieldValue("state", "");
                      setSelectedCountry(
                        csc
                          .getAllCountries()
                          .find((country) => country.name === option.value)
                      );
                    }}
                  />
                </div>
                <div className="col-lg-3 py-1">
                  <Select
                    name="state"
                    label="State/Province"
                    labelClasses="required-field"
                    placeholder="Select State/Province..."
                    isDisabled={!selectedCountry}
                    options={states.map((state) => ({
                      // Jamaica's ISO parish codes are numeric and meaningless
                      // when displayed; store the parish name instead. US/CA
                      // keep their standard 2-letter codes.
                      value:
                        selectedCountry && selectedCountry.isoCode === "JM"
                          ? state.name
                          : state.isoCode,
                      label: state.name,
                    }))}
                  />
                </div>
                <div className="col-lg-3 py-1">
                  <Field
                    name="zip"
                    label="Zip/Postal Code"
                    labelClasses={
                      hasNoPostalCode(values.country) ? undefined : "required-field"
                    }
                    placeholder="Zip/Postal Code"
                    type="text"
                    component={Input}
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-lg-6 py-1">
                  <Field
                    name="primary_email"
                    label="Primary Email"
                    labelClasses="required-field"
                    placeholder="school@example.com"
                    type="email"
                    component={Input}
                  />
                </div>
                <div className="col-lg-6 py-1">
                  <Field
                    name="primary_phone"
                    label="Primary Phone"
                    labelClasses="required-field"
                    placeholder="(123) - 456 - 7890"
                    type="text"
                    component={Input}
                    onChange={(e) => {
                      const formattedValue = getFormattedNumber(e.target.value);
                      setFieldValue("primary_phone", formattedValue);
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                style={{ display: "none" }}
                ref={btnRef}
                onSubmit={() => {
                  handleSubmit();
                }}
              />
            </Form>
          </fieldset>
        )}
      </Formik>
    </>
  );
}